<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges"></alert-unsaved-changes>
    <v-progress-linear
      v-show="loading || resourceLoading"
      color="neutral400"
      indeterminate
      rounded
      height="3"
    ></v-progress-linear>
    <v-form
      v-show="!loading && !resourceLoading"
      ref="form"
      v-model="valid"
      :lazy-validation="false"
    >
      <v-row>
        <v-col cols="5">
          <label>{{ $t('scientific-domain') }}</label>
          <p class="label-info">{{ $t('scientific-domain-message') }}</p>
        </v-col>
        <v-col cols="7">
          <label>{{ $t('scientific-subdomain') }}</label>
          <p class="label-info">{{ $t('scientific-subdomain-message') }}</p>
        </v-col>
      </v-row>
      <v-row v-for="(scientific, i) in scientifics" :key="'domain-' + i">
        <v-col cols="5">
          <v-autocomplete
            v-model="scientific.domain"
            :items="domains"
            outlined
            :rules="requiredRule()"
            :disabled="!edit"
            class="required"
            :placeholder="$t('please-select-domain')"
            data-cy="scientificDomain"
            v-on:change="filterSubDomains(i)"
            hide-details
          ></v-autocomplete>
        </v-col>
        <v-col cols="5">
          <v-autocomplete
            v-model="scientific.subDomain"
            :items="scientific.selected"
            :item-value="'id'"
            :item-text="'subDomain'"
            return-object
            outlined
            :disabled="scientific.domain == null || !edit"
            :rules="requiredRule()"
            :placeholder="$t('please-select-subdomain')"
            data-cy="scientificDomain"
          ></v-autocomplete>
        </v-col>
        <v-col cols="2">
          <v-btn v-if="edit" fab small text @click="removeScientific(i)">
            <v-icon>
              $vuetify.icons.trash
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-row class="links" v-if="edit">
        <v-col cols="12" sm="12" md="6">
          <v-btn
            depressed
            rounded
            large
            color="primary200"
            class="white--text font-weight-bold"
            @click="addScientific()"
          >
            <v-icon left>
              $vuetify.icons.plus
            </v-icon>
            <span class="text-decoration-underline text-transform-none font-weight-bold">
              {{ $t('add-scientific') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="5">
          <label>{{ $t('category') }}</label>
          <p class="label-info">{{ $t('category-message') }}</p>
        </v-col>
        <v-col cols="7">
          <label>{{ $t('subcategory') }}</label>
          <p class="label-info">{{ $t('subcategory-message') }}</p>
        </v-col>
      </v-row>
      <v-row v-for="(category, i) in categoriesSelected" :key="'category-' + i">
        <v-col cols="5">
          <v-autocomplete
            v-model="category.category"
            :items="categories"
            outlined
            :rules="requiredRule()"
            :disabled="!edit"
            class="required"
            :placeholder="$t('please-select-category')"
            data-cy="category"
            v-on:change="filterSubCategories(i)"
          ></v-autocomplete>
        </v-col>
        <v-col cols="5">
          <v-autocomplete
            v-model="category.subCategory"
            :items="category.selected"
            :item-value="'id'"
            :item-text="'subCategory'"
            return-object
            outlined
            :disabled="category.category == null || !edit"
            :rules="requiredRule()"
            :placeholder="$t('please-select-subcategory')"
            data-cy="subcategory"
          ></v-autocomplete>
        </v-col>
        <v-col cols="2">
          <v-btn v-if="edit" fab small text @click="removeCategory(i)">
            <v-icon>
              $vuetify.icons.trash
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-row class="links" v-if="edit">
        <v-col cols="12" sm="12" md="6">
          <v-btn
            depressed
            rounded
            large
            color="primary200"
            class="white--text font-weight-bold"
            @click="addCategory()"
          >
            <v-icon left>
              $vuetify.icons.plus
            </v-icon>
            <span class="text-decoration-underline text-transform-none font-weight-bold">
              {{ $t('add-category') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="!next">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel :key="1">
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pt-10 pb-7">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ 'Access Modes' }}</label>
                    <p class="label-info fixed-label-height">
                      Eligibility/criteria for granting access to the Resource to users
                      (excellence-based, free-conditionally, free etc.).
                    </p>
                    <v-select
                      v-model="resource.accessModes"
                      :items="accessModes"
                      clearable
                      :placeholder="'Select Access Modes'"
                      :item-value="'id'"
                      :item-text="'name'"
                      multiple
                      return-object
                      outlined
                      :disabled="!edit"
                    >
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ 'Access Types' }}</label>
                    <p class="label-info fixed-label-height">
                      The way a user can access the Service (Remote, Physical, Virtual, etc.).
                      <br /><br />
                    </p>
                    <v-select
                      v-model="resource.accessTypes"
                      :items="accessTypes"
                      clearable
                      :placeholder="'Select Access Types'"
                      :item-value="'id'"
                      :item-text="'name'"
                      multiple
                      return-object
                      outlined
                      :disabled="!edit"
                    >
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ 'Targets Users' }}</label>
                    <p class="label-info">
                      Specify Target Users of the Resource (Researchers, Students, Businesses etc.).
                    </p>
                    <v-select
                      v-model="resource.targets"
                      :items="targets"
                      clearable
                      :placeholder="'Select Target Users'"
                      :item-value="'id'"
                      :item-text="'name'"
                      multiple
                      return-object
                      outlined
                      :disabled="!edit"
                    >
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('tags') }}</label>
                    <p class="label-info">{{ $t('tags-message') }}</p>
                    <v-combobox
                      v-model="resource.tags"
                      clearable
                      :placeholder="$t('tags')"
                      multiple
                      persistent-hint
                      small-chips
                      return-object
                      outlined
                      :disabled="!edit"
                      data-cy="tags"
                      hint="Type to create a new tag"
                    >
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>
                              Type and press <kbd>enter</kbd> to create a new tag
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template v-slot:selection="{ attrs, item, parent, selected }">
                        <v-chip
                          v-bind="attrs"
                          :input-value="selected"
                          :close="true"
                          @click:close="removeChip(item)"
                        >
                          <span class="pr-2">
                            {{ item }}
                          </span>
                        </v-chip>
                      </template>
                    </v-combobox>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-form>
    <!--BUTTONS-->
    <v-row class="my-5" v-if="next" justify="end">
      <v-btn outlined class="black--text font-weight-bold mx-2" rounded x-large @click="back()">
        <v-icon color="black">$vuetify.icons.back</v-icon>
        {{ $t('back') }}
      </v-btn>
      <v-btn
        depressed
        rounded
        x-large
        color="primary200"
        class="white--text font-weight-bold"
        @click="add()"
        :disabled="!valid || !scientifics.length || !categoriesSelected.length"
        >{{ $t('next') }} <v-icon color="white">$vuetify.icons.next</v-icon>
      </v-btn>
    </v-row>
    <v-row class="my-5" v-if="!next && edit" justify="end">
      <v-btn outlined class="black--text font-weight-bold mx-2" rounded x-large @click="cancel()">
        {{ $t('cancel') }}
      </v-btn>
      <v-btn
        depressed
        rounded
        x-large
        color="primary200"
        class="white--text font-weight-bold"
        @click="add()"
        :disabled="!valid || !scientifics.length || !categoriesSelected.length"
        >{{ $t('update') }}
      </v-btn>
    </v-row>
  </v-container>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import base from '@/services/base-service.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';

export default {
  name: 'ResourceClassificationInfo',
  mixins: [util, base],
  props: {
    admin: {
      type: Boolean,
      default: false,
    },
    id: {
      type: [Number, String],
      default: null,
    },
    edit: {
      type: Boolean,
      default: true,
    },
    next: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    AlertUnsavedChanges,
  },
  data() {
    return {
      valid: false,
      loadingComponents: -7,
      unsavedChanges: false,
      resource: {
        id: null,
        tags: [],
        scientificsIds: [],
        structureType: [],
        scientifics: [],
        categories: [],
        categoriesIds: [],
        accessTypes: [],
        accessModes: [],
        targets: [],
      },
      scientifics: [],
      domains: [],
      subdomains: [],
      categoriesSelected: [],
      categories: [],
      subCategories: [],
      accessTypes: [],
      accessModes: [],
      targets: [],
      resourceLoading: true,
    };
  },
  created() {
    this.getScientificDomains().then(res => {
      this.domains = res.data.content;
      this.loadingComponents++;
    });
    this.getScientificSubdomains().then(res => {
      this.subdomains = res.data.content;
      this.loadingComponents++;
    });
    this.axios.get('api/v1/resource-categories').then(res => {
      this.categories = res.data.content;
      this.loadingComponents++;
    });
    this.axios.get('/api/v1/resource-subcategories').then(res => {
      this.subCategories = res.data.content;
      this.loadingComponents++;
    });
    this.getAccessTypes().then(res => {
      this.accessTypes = res.data.content;
      this.loadingComponents++;
    });
    this.getAccessModes().then(res => {
      this.accessModes = res.data.content;
      this.loadingComponents++;
    });
    this.getTargets().then(res => {
      this.targets = res.data.content;
      this.loadingComponents++;
    });
  },
  methods: {
    cancel() {
      this.$emit('cancel', true);
      this.unsavedChanges = false;
    },
    back() {
      console.log('back!');
      this.$emit('tab', 1);
    },
    addDefaultValue(i) {
      //add Generic as Default
      this.scientifics[i].selected = this.subdomains.filter(
        item => item.domain === this.scientifics[i].domain,
      );
      this.scientifics[i].subDomain = this.scientifics[i].selected[0];
    },
    filterSubDomains(i) {
      console.log('[Domain] Domain changed');
      this.scientifics[i].subDomain = null;
      this.scientifics[i].selected = this.subdomains.filter(
        item => item.domain === this.scientifics[i].domain,
      );
    },
    addScientific() {
      this.scientifics.push({ domain: 'Generic', subDomain: null, selected: [] });
      this.addDefaultValue(this.scientifics.length - 1);
    },
    removeScientific(index) {
      this.scientifics.splice(index, 1);
    },
    addDefaultCategory(i) {
      //add Generic as Default
      this.categoriesSelected[i].selected = this.subCategories.filter(
        item => item.category === this.categoriesSelected[i].category,
      );
      this.categoriesSelected[i].subCategory = this.categoriesSelected[i].selected[0];
    },
    filterSubCategories(i) {
      console.log('[Category] Category changed');
      this.categoriesSelected[i].subCategory = null;
      this.categoriesSelected[i].selected = this.subCategories.filter(
        item => item.category === this.categoriesSelected[i].category,
      );
    },
    addCategory() {
      this.categoriesSelected.push({
        category: null,
        subCategory: null,
        selected: [],
      });
      this.addDefaultCategory(this.categoriesSelected.length - 1);
    },
    removeCategory(index) {
      this.categoriesSelected.splice(index, 1);
    },
    add() {
      EventBus.$emit('waiting', true);
      this.resource.scientificsIds = [];
      this.scientifics.forEach(s => {
        this.resource.scientificsIds.push(s.subDomain.id);
      });
      this.resource.categoriesIds = [];
      this.categoriesSelected.forEach(s => {
        this.resource.categoriesIds.push(s.subCategory.id);
      });
      this.axios
        .post('/api/v1/resources/classification-info', this.resource)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('resource-updated'),
          });
          this.unsavedChanges = false;
          this.$emit('tab', 3);
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    removeChip(itemText) {
      this.resource.tags.forEach(obj => {
        if (obj === itemText) {
          this.resource.tags.splice(this.resource.tags.indexOf(obj), 1);
        }
      });
      this.resource.tags = [...this.resource.tags];
    },
  },
  computed: {
    loading() {
      return this.loadingComponents !== 0;
    },
  },
  watch: {
    loading(value) {
      if (value === false) {
        console.log('Loading');
        if (this.id != null) {
          this.axios
            .get('/api/v1/resources/' + this.id)
            .then(res => {
              this.resource = res.data;
              this.resource.scientifics.forEach(scientific => {
                let subScientifics = this.subdomains.filter(
                  item => item.domain === scientific.domain,
                );
                this.scientifics.push({
                  selected: subScientifics,
                  domain: scientific.domain,
                  subDomain: scientific,
                });
              });
              this.resource.categories.forEach(category => {
                let subCategories = this.subCategories.filter(
                  item => item.category === category.category,
                );
                this.categoriesSelected.push({
                  selected: subCategories,
                  category: category.category,
                  subCategory: category,
                });
              });
              if (!this.scientifics.length) {
                this.addScientific();
              }
              if (!this.categoriesSelected.length) {
                this.addCategory();
              }
              this.$nextTick(() => {
                this.unsavedChanges = false;
              });
              this.$nextTick(() => {
                this.resourceLoading = false;
              });
            })
            .catch(error => {
              console.log({ error });
            });
        } else {
          this.resourceLoading = false;
        }
      }
    },
    resource: {
      handler: function(val, oldVal) {
        console.log('[Resource] Value changed');
        if (oldVal.id) {
          this.unsavedChanges = true;
        }
      },
      deep: true,
    },
    categoriesSelected: {
      handler: function(val) {
        console.log('[Resource] Value changed');
        if (val.filter(e => e.category === 'Instruments & Equipment').length > 0) {
          this.$emit('isEquipment', true);
        } else {
          this.$emit('isEquipment', false);
        }
        this.unsavedChanges = true;
      },
      deep: true,
    },
    scientifics: {
      handler: function() {
        console.log('[Resource] Value changed');
        this.unsavedChanges = true;
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.fixed-label-height {
  min-height: 4em;
}
</style>
